import { useState, useEffect, useCallback } from 'react';
import { authFetch, handleResponse } from '@/utils/auth-fetch';

// All available section IDs (must match backend AeoPageMerger.ALL_SECTIONS)
const ALL_SECTIONS = [
  'summary',
  'best_for',
  'problem_solver',
  'use_cases',
  'buyer_profiles',
  'benefits_explanation',
  'specifications',
  'comparison',
  'comparison_products',
  'smart_alternatives',
  'faq',
  'recommendations',
  'limitations'
];

const STORAGE_KEY = 'aeo_section_config';

export type AeoMode = 'full' | 'partial';

interface SectionConfig {
  mode: AeoMode;
  preset: string;
  selectedSections: string[];
  lastUpdated: string | null;
}

interface UseSectionSelectionReturn {
  mode: AeoMode;
  setMode: (mode: AeoMode) => void;
  preset: string;
  setPreset: (preset: string) => void;
  selectedSections: string[];
  setSelectedSections: (sections: string[]) => void;
  isLoading: boolean;
  error: string | null;
  saveConfig: () => Promise<void>;
  resetToDefaults: () => void;
  getAeoSectionConfig: () => { mode: AeoMode; sections: string[] } | null;
}

const defaultConfig: SectionConfig = {
  mode: 'full',
  preset: 'full',
  selectedSections: ALL_SECTIONS,
  lastUpdated: null,
};

export function useSectionSelection(): UseSectionSelectionReturn {
  const [mode, setModeState] = useState<AeoMode>('full');
  const [preset, setPresetState] = useState('full');
  const [selectedSections, setSelectedSectionsState] = useState<string[]>(ALL_SECTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, try localStorage for immediate response
      const localConfig = localStorage.getItem(STORAGE_KEY);
      if (localConfig) {
        try {
          const parsed = JSON.parse(localConfig) as SectionConfig;
          setModeState(parsed.mode || 'full');
          setPresetState(parsed.preset || 'full');
          setSelectedSectionsState(parsed.selectedSections || ALL_SECTIONS);
        } catch (e) {
          console.warn('Invalid localStorage config, ignoring');
        }
      }

      // Then, try to load from backend (source of truth)
      const response = await authFetch('/api/aeo-benchmark/section-config');
      const data = await handleResponse<{ config: SectionConfig }>(response);

      if (data.config) {
        setModeState(data.config.mode || 'full');
        setPresetState(data.config.preset || 'full');
        setSelectedSectionsState(data.config.selectedSections || ALL_SECTIONS);

        // Update localStorage with backend data
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data.config));
      }
    } catch (err: any) {
      console.warn('Failed to load section config from backend:', err.message);
      // Keep localStorage values if backend fails
    } finally {
      setIsLoading(false);
      setInitialized(true);
    }
  };

  // Persist to localStorage whenever values change (after initial load)
  useEffect(() => {
    if (!initialized) return;

    const config: SectionConfig = {
      mode,
      preset,
      selectedSections,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [mode, preset, selectedSections, initialized]);

  const setMode = useCallback((newMode: AeoMode) => {
    setModeState(newMode);
    if (newMode === 'full') {
      setPresetState('full');
      setSelectedSectionsState(ALL_SECTIONS);
    }
  }, []);

  const setPreset = useCallback((newPreset: string) => {
    setPresetState(newPreset);
    if (newPreset !== 'custom') {
      // Preset sections will be set by SectionSelector component
    }
  }, []);

  const setSelectedSections = useCallback((sections: string[]) => {
    setSelectedSectionsState(sections);
    // Check if it matches 'full' preset
    if (sections.length === ALL_SECTIONS.length &&
        ALL_SECTIONS.every(s => sections.includes(s))) {
      setPresetState('full');
    } else if (preset !== 'custom') {
      setPresetState('custom');
    }
  }, [preset]);

  const saveConfig = useCallback(async () => {
    setError(null);
    try {
      await authFetch('/api/aeo-benchmark/section-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          preset,
          selectedSections,
        }),
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
      throw err;
    }
  }, [mode, preset, selectedSections]);

  const resetToDefaults = useCallback(() => {
    setModeState(defaultConfig.mode);
    setPresetState(defaultConfig.preset);
    setSelectedSectionsState(defaultConfig.selectedSections);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultConfig));
  }, []);

  const getAeoSectionConfig = useCallback(() => {
    if (mode === 'full') {
      return null; // No config needed for full mode
    }
    return {
      mode,
      sections: selectedSections,
    };
  }, [mode, selectedSections]);

  return {
    mode,
    setMode,
    preset,
    setPreset,
    selectedSections,
    setSelectedSections,
    isLoading,
    error,
    saveConfig,
    resetToDefaults,
    getAeoSectionConfig,
  };
}
